import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { listProjectVitalTasks } from '../services/projectVitalService';

function formatDueDate(value: string | null) {
  if (!value) {
    return null;
  }

  return new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(`${value}T00:00:00`));
}

export default function ProjectVitalHomeView() {
  const tasksQuery = useQuery({
    queryKey: ['project-vital', 'tasks'],
    queryFn: listProjectVitalTasks,
  });

  const pendingTasks = (tasksQuery.data ?? [])
    .filter(
      (task) =>
        task.status === 'pendiente' ||
        task.status === 'en_curso',
    )
    .slice(0, 6);

  return (
    <Stack spacing={3}>
      <Box>
        <Typography
          component="h1"
          sx={{
            fontWeight: 850,
            fontSize: { xs: '1.8rem', md: '2.2rem' },
            letterSpacing: '-0.7px',
          }}
        >
          Inicio
        </Typography>

        <Typography color="text.secondary" sx={{ mt: 0.75 }}>
          Lo que necesitas saber ahora.
        </Typography>
      </Box>

      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2.25, sm: 2.75 },
          borderRadius: 3,
          bgcolor: '#1C293A',
          borderColor: 'rgba(255,255,255,0.08)',
        }}
      >
        <Typography
          component="h2"
          sx={{
            fontWeight: 750,
            fontSize: '1rem',
          }}
        >
          Pendiente
        </Typography>

        {tasksQuery.isLoading ? (
          <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : tasksQuery.isError ? (
          <Typography color="error" sx={{ mt: 1.5 }}>
            No se pudieron cargar las tareas.
          </Typography>
        ) : pendingTasks.length === 0 ? (
          <Typography color="text.secondary" sx={{ mt: 1.5 }}>
            No tienes tareas pendientes.
          </Typography>
        ) : (
          <Stack spacing={1.1} sx={{ mt: 1.5 }}>
            {pendingTasks.map((task) => (
              <Box
                key={task.id}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: 'rgba(255,255,255,0.025)',
                  border: '1px solid rgba(255,255,255,0.06)',
                }}
              >
                <Stack
                  direction="row"
                  alignItems="flex-start"
                  justifyContent="space-between"
                  spacing={2}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        lineHeight: 1.35,
                      }}
                    >
                      {task.title}
                    </Typography>

                    <Typography
                      color="text.secondary"
                      sx={{
                        mt: 0.4,
                        fontSize: '0.8rem',
                      }}
                    >
                      {task.area}
                      {task.due_date
                        ? ` · ${formatDueDate(task.due_date)}`
                        : ''}
                    </Typography>
                  </Box>

                  {task.is_important ? (
                    <Chip
                      label="Importante"
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  ) : null}
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: 'repeat(2, minmax(0, 1fr))',
          },
          gap: 2,
        }}
      >
        {[
          ['Hoy', 'Aquí aparecerán tus eventos de Google Calendar.'],
          ['Requiere atención', 'Asuntos bloqueados, esperando o con fechas cercanas.'],
          ['Finanzas del mes', 'Ingresos, gastos y balance mensual.'],
          ['Actividad reciente', 'Últimos cambios registrados en Proyecto Vital.'],
        ].map(([title, description]) => (
          <Paper
            key={title}
            variant="outlined"
            sx={{
              p: { xs: 2.25, sm: 2.75 },
              borderRadius: 3,
              minHeight: 132,
              bgcolor: '#1C293A',
              borderColor: 'rgba(255,255,255,0.08)',
            }}
          >
            <Typography
              component="h2"
              sx={{
                fontWeight: 750,
                fontSize: '1rem',
              }}
            >
              {title}
            </Typography>

            <Typography
              color="text.secondary"
              sx={{
                mt: 1,
                lineHeight: 1.6,
                fontSize: '0.9rem',
              }}
            >
              {description}
            </Typography>
          </Paper>
        ))}
      </Box>
    </Stack>
  );
}
